import React from 'react';
// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from '!mapbox-gl'; // https://docs.mapbox.com/mapbox-gl-js/guides/install/#excluding-mapbox-gl-js-explicitly-from-transpilation
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { connect } from 'react-redux';
import distance from '@turf/distance';
import destination from '@turf/destination';
import bearing from '@turf/bearing';
import geofences from 'APP_ROOT/data/geofences';
import ProgressOverlay from 'APP_ROOT/components/common/ProgressOverlay';
import MapboxHelper from 'APP_ROOT/components/mapbox/mapboxHelperDashboard';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { getCurrentLocale, strings } from 'APP_ROOT/localization';
import MapboxPolygonGeofenceControl from "../mapboxExtendedControls/mapboxPolygonGeofenceControl"
import ConfirmDialog from 'APP_ROOT/components/common/ConfirmDialog';
import { NULL } from 'node-sass';


const MIN_DIAMETER_PX = 96;
const DEFAULT_DIAMETER_PX = MIN_DIAMETER_PX * 3.33333; // 3.33333 is a ratio which will default to a 100 m wide editor circle, but allow it to be shrunk to minimum 30 m at the same zoom level: redux const DEFAULT_RADIUS divided by MIN_RADIUS_KM (100 meters / 30 meters)

const MIN_RADIUS_KM = 0.03;
const MAX_RADIUS_KM = 100;

// Todo : Move the whole Polygon drawing logic to this component (like what geofenceEditor.js does for circle geofences)
class GeofenceEditorBackground extends React.PureComponent {

    constructor(props) {
        super(props);
        this.state = {
            geoCoderIsUsed: false,
            showOnboardingMessage: true,
            confirmRemoveOpen: false,
            confirmRemoveMessage: "",
            mapZoomExtentsAreSet: false,
        };
        
        this.MapboxPolygonGeofenceControl = null;
        this.polygonDraw = null;
    }

    polygonIsDrawnOnMap = () => {
        const features = this.polygonDraw.getAll().features;
        return features.length > 0 && features[0].geometry.coordinates[0].length > 3;
    }
    
    mapboxPolygonDrawButtonCallback = () => {
        const currentPolygons = this.polygonDraw.getAll();

        if(currentPolygons.features.length > 0 && currentPolygons.features[0].geometry.coordinates[0].length > 3){
            this.polygonDraw.changeMode('direct_select', { featureId: currentPolygons.features[0].id });
            return;
        }

        this.polygonDraw.changeMode('draw_polygon');
    }

    mapboxPolygonTrashButtonCallback = () => {
        const mode = this.polygonDraw.getMode();
        
        const numberOfPolygonsVerteces = this.polygonDraw.getAll().features[0].geometry.coordinates[0].length;
        const numberOfSelectedVerteces = this.polygonDraw.getSelectedPoints().features.length;

        const msg = (mode === "direct_select" && numberOfPolygonsVerteces > 4 && numberOfSelectedVerteces > 0) ? strings.geofence_remove_vertex_prompt : strings.geofence_remove_prompt;
           
        this.openConfirmRemoveDialog(msg);
    }

    removeGeofence = () => {
        const mode = this.polygonDraw.getMode();
        
        const numberOfPolygonsVerteces = this.polygonDraw.getAll().features[0].geometry.coordinates[0].length;
        const numberOfSelectedVerteces = this.polygonDraw.getSelectedPoints().features.length;

        // if number of verteces is 4 removing a vertex is not allowed (because a polygon must have at least 4 verteces)
        if(mode === "direct_select" &&  numberOfPolygonsVerteces > 4 && numberOfSelectedVerteces > 0) {
            this.polygonDraw.trash();
        }
        else{
            this.polygonDraw.deleteAll();
            this.polygonDraw.changeMode('draw_polygon');
            this.onMapDrawEvent("draw.delete");
        }

        this.closeConfirmRemoveDialog();
    }

    mapboxCloseOnboardingMessageCallback = () => {
        this.setState({showOnboardingMessage: false});
    }

    // replace polygon drawn at original location with a new one at the new location (when geoCoder is used))
    moveThePolygon = (coordinates) => {
        const currentFeature = this.polygonDraw.getAll().features[0];
        this.polygonDraw.set({
            type: 'FeatureCollection',
            features: [{
                type: 'Feature',
                properties: currentFeature.properties,
                id: currentFeature.id,
                geometry: { type: 'Polygon', coordinates }
            }]
        });
    }

    mapboxManagePolygonControl = () => {

        this.mapboxAddPolyganControl();

        if(this.props.isGeofenceImported())
            return this.renderImportedPolygon();
        
        if(this.props.geofenceId !== undefined)
            return this.renderEditingPolygon();

    }

    renderImportedPolygon(){
        this.renderImportingOrEditingPolygon("imported");
    }

    renderEditingPolygon = () => {
        this.renderImportingOrEditingPolygon("editing");
    }

    renderImportingOrEditingPolygon = (polygonType) => {

        if(!this.props.coordinates) return;

        if(!this.state.importingOrEditingPolygonRendered){
            const coordinates = this.props.editingGeometry().geometry.coordinates;

            const geofenceId = this.props.geofenceId || 'imported';
            this.polygonDraw.set({
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        properties: {},
                        id: geofenceId,
                        geometry: { type: 'Polygon', coordinates: coordinates}
                    }
                ]
            });

            this.polygonDraw.changeMode('direct_select', { featureId: geofenceId });

            this.onMapDrawEvent(`draw.${polygonType}`);

            this.setState({importingOrEditingPolygonRendered: true});
        }
    }

    mapboxAddGeocoderControl = (mapboxMap) => {
        // adding a control to top/top-center is not supported! (https://github.com/mapbox/mapbox-gl-js/issues/3039)
        this.geocoderSearchControl = new MapboxGeocoder({
            accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
            mapboxgl: mapboxgl,
            language: getCurrentLocale(),
            marker: true //https://github.com/mapbox/mapbox-gl-geocoder/blob/master/API.md#parameters
        });
        this.geocoderSearchControl.on("result", this.onGeocoderResult);
        mapboxMap.addControl(
            this.geocoderSearchControl,
            'top-left' // hijacked and set to top/top-center behaviour in _geofenceEditor.scss
        );
    }

    mapboxRemoveGeocoderControl = () => {
        this.geocoderSearchControl.off("result", this.onGeocoderResult);
        this.props.mapboxMap.removeControl(this.geocoderSearchControl);
    }

    mapboxAddPolyganControl = () => {
        const { mapboxMap } = this.props;
        if(!this.polygonDraw){
            this.polygonDraw = new MapboxDraw({
                displayControlsDefault: false,
                defaultMode: 'draw_polygon',
            });
            mapboxMap.addControl(this.polygonDraw);
            this.props.setGeofenceLayersVisible(false);
        }

        const showOnboardingMessage = this.state.showOnboardingMessage !== undefined ? this.state.showOnboardingMessage : true;
        const disableDrawButton = false; //this.polygonDraw.getAll().features.length > 0;
        const disableTrashButton = false; // !disableDrawButton;

        if(mapboxMap.hasControl(this.MapboxPolygonGeofenceControl))
            mapboxMap.removeControl(this.MapboxPolygonGeofenceControl);
        
        this.MapboxPolygonGeofenceControl = new MapboxPolygonGeofenceControl(this.mapboxPolygonDrawButtonCallback, this.mapboxPolygonTrashButtonCallback, this.mapboxCloseOnboardingMessageCallback, disableDrawButton, disableTrashButton, showOnboardingMessage);
        mapboxMap.addControl(this.MapboxPolygonGeofenceControl);
    }

    mapboxRemovePolyganControl = () => {
        const { mapboxMap } = this.props;
        if(this.polygonDraw){
            this.polygonDraw.deleteAll();
            mapboxMap.removeControl(this.polygonDraw);
            this.polygonDraw = null;

            mapboxMap.removeControl(this.MapboxPolygonGeofenceControl);
            this.MapboxPolygonGeofenceControl = null;

            mapboxMap.getCanvas().style.cursor = '';
            this.setState({importingOrEditingPolygonRendered: false});

            this.props.setGeofenceLayersVisible(true);
        }
    }

    openConfirmRemoveDialog = (msg) => {
        this.setState({
            confirmRemoveOpen: true,
            confirmRemoveMessage: msg
        });
    }

    closeConfirmRemoveDialog = () => this.setState({ confirmRemoveOpen: false });

    onMapDrawEvent = (event) => {
        var data = this.polygonDraw.getAll();

        if(event === "draw.delete"){
            this.props.setGeofenceValidity(false);
            this.props.setGeofenceCoordinates(null, null);
            this.setState({importingOrEditingPolygonRendered: false})
            return;
        }

        const hasOverlaps = MapboxHelper.checkGeofenceForOverlapingPolylines(data.features[0].geometry.coordinates[0]);
        const geofenceArea = MapboxHelper.calcPolygonArea(data);
    
        if(hasOverlaps){
            this.props.setGeofenceValidity(false, "intersect");
        }  
        else if(geofenceArea < 2827){ // m2
            this.props.setGeofenceValidity(false, "small");
        }else{
            this.props.setGeofenceValidity(true);
        }
        
        const polygonCenter = MapboxHelper.getCenterOfPolygon(data.features[0].geometry.coordinates);
        this.props.setGeofenceCoordinates(data.features[0].geometry.coordinates, polygonCenter);
        
        if(event === "draw.create" || (event === "draw.update" && geofenceArea / 1000000 !== this.props.geofenceAreaInKm2())){
            this.props.setGeofenceArea(geofenceArea);
        }

        if(event === "draw.imported"){
            this.props.setGeofenceArea(geofenceArea);
        }

    }

    onGeocoderResult = (data) => {
        this.props.onGeofenceAddressChange(data.result.place_name);
        this.setState({ geoCoderIsUsed: true });
    }

    onMapChange = () => {
        // if map move event is not related to geocoder search, do not proceed
        if(!this.state.geoCoderIsUsed) return;
        
        
        // if a polygon is not drawn, do not proceed
        if(!this.polygonIsDrawnOnMap()) return;
        
        // to move polygon we need distance and direction/bearing between the center of the polygon and the new coordinates
        // https://turfjs.org/docs/#destination
        
        const polygonCoords = this.props.coordinates;

        const mapCenter = Object.values(this.props.mapboxMap.getCenter());
        const polygonCenter = MapboxHelper.getCenterOfPolygon(polygonCoords);

        const distancebetweenCenters = distance(polygonCenter, mapCenter);
        const bearingBetweenCenters = bearing(polygonCenter, mapCenter);

        // calculate new coordinates for the polygon (each point separately)
        const newCoords = polygonCoords[0].map(point => {
            const newPOint = destination(point, distancebetweenCenters, bearingBetweenCenters);
            return newPOint.geometry.coordinates;
        }); 

        this.props.onGeofencecoordinatesChange([newCoords], mapCenter);
        // change coordinates of drawn polygon on the map
        this.moveThePolygon([newCoords]);

        this.setState({ geoCoderIsUsed: false });
    }

    onPolygonCreate = (e) => {
        this.onMapDrawEvent("draw.create");
    }

    onPolygonUpdate = (e) => {
        this.onMapDrawEvent("draw.update");
    }

    onPolygonModeChange = (e) => {
        const { mapboxMap } = this.props;
        switch(e.mode){
            case  "draw_polygon":
                mapboxMap.getCanvas().style.cursor = 'crosshair';
            default:
                const {geofenceId} = this.props;
                const geofenceColor = this.props.geofenceColor(geofenceId);
                mapboxMap.setPaintProperty('gl-draw-polygon-fill-inactive.hot', 'fill-color', geofenceColor);
                mapboxMap.setPaintProperty('gl-draw-polygon-fill-inactive.cold', 'fill-color', geofenceColor);
                mapboxMap.setPaintProperty('gl-draw-polygon-stroke-inactive.hot', 'line-color', geofenceColor);
                mapboxMap.setPaintProperty('gl-draw-polygon-stroke-inactive.cold', 'line-color', geofenceColor);
                mapboxMap.getCanvas().style.cursor = '';
        }
    }

    componentDidMount = () => {
        const { mapboxMap } = this.props;

        mapboxMap.on("moveend", this.onMapChange);
        mapboxMap.on("draw.create", this.onPolygonCreate);
        mapboxMap.on("draw.update", this.onPolygonUpdate);
        mapboxMap.on("draw.modechange", this.onPolygonModeChange);

        this.mapboxManagePolygonControl();
        
        this.mapboxAddGeocoderControl(mapboxMap);

        if (this.props.coordinates){
            //this does the setMapZoomExtents as well
            this.zoomToFitGeofence();
        }
        else{
            this.setMapZoomExtents();
        }
    }

    componentDidUpdate = (prevProps, prevState) => {
        this.mapboxManagePolygonControl();

        if (!prevProps.coordinates && this.props.coordinates) {
            this.zoomToFitGeofence();
        }
    }

    componentWillUnmount = () => {
        this.props.mapboxMap.off("moveend", this.onMapChange);
        this.props.mapboxMap.off("draw.create", this.onPolygonCreate);
        this.props.mapboxMap.off("draw.update", this.onPolygonUpdate);
        this.props.mapboxMap.off("draw.modechange", this.onPolygonModeChange);
        this.mapboxRemovePolyganControl();
        this.mapboxRemoveGeocoderControl();
        this.restoreInitialMapZoomExtents();
    }

    zoomToFitGeofence = () => {
        const { mapboxMap, coordinates } = this.props;
        const cameraOptions = this.cameraForGeofence(mapboxMap, coordinates, DEFAULT_DIAMETER_PX);
        cameraOptions.pitch = 0;
        this.setMapZoomExtents();
        mapboxMap.easeTo(cameraOptions, {
            startedBy: 'zoomToFitGeofence',
            overrideDefaultSize: cameraOptions.zoom > mapboxMap.getMaxZoom()
        });
    }

    cameraForGeofence = (mapboxMap, coordinates, diameterInPx) => {
        const bounds = MapboxHelper.getBoundsFromPolygon(coordinates);
        const mapContainer = mapboxMap.getCanvas();

        let verticalPadding = diameterInPx ? (mapContainer.clientHeight - diameterInPx) / 2 : 0;
        if (verticalPadding < 1) verticalPadding = 0;

        let horizontalPadding = diameterInPx ? (mapContainer.clientWidth - diameterInPx) / 2 : 0;
        if (horizontalPadding < 1) horizontalPadding = 0;
        
        return mapboxMap.cameraForBounds(bounds, {
            padding: {
                top: verticalPadding,
                right: horizontalPadding,
                bottom: verticalPadding,
                left: horizontalPadding
            }
        });
    }

    calculateZoom = (mapboxMap, coordinates, radiusInKm, diameterInPx) => {
        let bounds;
        if(coordinates[0].length > 3){
            const polygonCenter = MapboxHelper.getCenterOfPolygon(coordinates);
            bounds = MapboxHelper.getBoundsFromRadiusInKm(polygonCenter, radiusInKm);
        }
        else{
            bounds = MapboxHelper.getBoundsFromRadiusInKm(coordinates, radiusInKm);
        }

        const mapContainer = mapboxMap.getCanvasContainer();

        let verticalPadding = diameterInPx ? (mapContainer.clientHeight - diameterInPx) / 2 : 0;
        if (verticalPadding < 1) verticalPadding = 0;

        let horizontalPadding = diameterInPx ? (mapContainer.clientWidth - diameterInPx) / 2 : 0;
        if (horizontalPadding < 1) horizontalPadding = 0;
        const camera = mapboxMap.cameraForBounds(bounds, {
            padding: {
                top: verticalPadding,
                right: horizontalPadding,
                bottom: verticalPadding,
                left: horizontalPadding
            }
        });

        return camera.zoom;
    }

    setMapZoomExtents = () => {
        if(this.state.mapZoomExtentsAreSet) return;

        const { coordinates, mapboxMap } = this.props;

        const zoomCoordinates = (!coordinates || !coordinates[0]) ? Object.values(mapboxMap.getCenter()) : coordinates;
        
        this._initialMapZoomExtents = [
            mapboxMap.getMinZoom(),
            mapboxMap.getMaxZoom()
        ];        
        
        const minZoom = this.calculateZoom(mapboxMap, zoomCoordinates, MAX_RADIUS_KM);
        const maxZoom = this.calculateZoom(mapboxMap, zoomCoordinates, MIN_RADIUS_KM, MIN_DIAMETER_PX);

        mapboxMap.setMinZoom(minZoom);
        mapboxMap.setMaxZoom(maxZoom);

        this.setState({ mapZoomExtentsAreSet: true });
    }

    restoreInitialMapZoomExtents = () => {
        if (this._initialMapZoomExtents) {
            const { mapboxMap } = this.props;
            const [ minZoom, maxZoom ] = this._initialMapZoomExtents;
            mapboxMap.setMinZoom(minZoom);
            mapboxMap.setMaxZoom(maxZoom);
            this._initialMapZoomExtents = null;
        }
    }

    render () {

        const { saving } = this.props;

        return (
            <>
                <div className={"geofence-editor edit-mode"}>
                    { saving && <ProgressOverlay/> }
                </div>            
                <ConfirmDialog
                    isOpen={this.state.confirmRemoveOpen}
                    title={this.state.confirmRemoveMessage}
                    onOk={this.removeGeofence}
                    onCancel={this.closeConfirmRemoveDialog}
                    onDismiss={this.closeConfirmRemoveDialog}
                />
            </>
        )
    }
}


const mapStateToProps = (state, ownProps) => {

    return {
        saving: geofences.Selectors.savingGeometryEdit(state, ownProps.geofenceId),
        coordinates: geofences.Selectors.geofenceCoordinates(state, ownProps.geofenceId),
        geojson: geofences.Selectors.geofenceGeojson(state, ownProps.geofenceId),
        isGeofenceImported: () => geofences.Selectors.geofenceIsImported(state),
        editingGeometry: () => geofences.Selectors.editingGeometry(state),
        geofenceAreaInKm2: (geofenceId) => geofences.Selectors.geofenceAreaInKm2(state, geofenceId),
        geofenceColor: (geofenceId) => geofences.Selectors.geofenceColor(state, geofenceId),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => { return {
    onGeofenceAddressChange: (address) => dispatch(
        geofences.Actions.editGeofenceAddress(address)
    ),
    onGeofencecoordinatesChange: (coordinates, center) => {
        // update the polygon in store with the new coordinates
        dispatch(geofences.Actions.editGeofenceCoordinates(coordinates, center));
    },
    setGeofenceValidity: (isValid, error = null) => dispatch(
        geofences.Actions.setGeofenceValidity(isValid, error)
    ),
    setGeofenceCoordinates: (coordinates, center) => dispatch(
        geofences.Actions.editGeofenceCoordinates(coordinates, center)
    ),
    setGeofenceArea: (area) => dispatch(
        geofences.Actions.editGeofenceArea(area)
    )
}};

export default connect(mapStateToProps, mapDispatchToProps)(GeofenceEditorBackground);